import { useState, type FormEventHandler, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type AxiosError } from "axios";

import { usePasswordResetConfirm } from "shared/apiHooks";
import { cn } from "shared/lib";

import { useMediaQuery } from "shared/hooks";

import { ResetPassword } from "@/components/auth";

export function ResetPasswordPage() {
  const navigate = useNavigate();

  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const uid = searchParams.get("uid") || "";

  const [errorMessage, setErrorMessage] = useState<ReactNode>("");

  const showErrorMessage = (error: AxiosError) => {
    console.log("<ResetPasswordPage> showErrorMessage()", error);
    if (typeof error.response?.data === "object") {
      const errors = Object.keys(error.response?.data || {}).flatMap(
        (fieldName) => {
          return error.response?.data![fieldName];
        },
      );

      setErrorMessage(
        <div className="text-left">
          {errors.map((error) => (
            <>
              {error}
              <br />
            </>
          ))}
        </div>,
      );
    } else {
      setErrorMessage(
        "There was a problem resetting your password, please contact support.",
      );
    }
  };

  const { isPending: isLoading, mutate: passwordResetConfirm } =
    usePasswordResetConfirm({
      onError(error) {
        console.error(
          "<ResetPasswordPage> usePasswordResetConfirm() onError()",
          error,
        );
        showErrorMessage(error);
      },
      onSuccess() {
        console.log(
          "<ResetPasswordPage> usePasswordResetConfirm() onSuccess()",
        );
        setErrorMessage("");
        navigate("/");
      },
    });

  const handleClickSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (token && uid) {
      const formData = new FormData(event.currentTarget);
      const password = formData.get("password") as string;
      const passwordConfirm = formData.get("passwordConfirm") as string;

      if (password !== passwordConfirm) {
        setErrorMessage("Passwords must match");
        return;
      }

      if (password === "" || passwordConfirm === "") {
        setErrorMessage("Passwords must not be blank");
        return;
      }

      passwordResetConfirm({
        body: {
          new_password1: password,
          new_password2: passwordConfirm,
          token,
          uid,
        },
      });
    }
  };

  return (
    <div
      className={cn(
        "flex grow flex-col overflow-y-auto px-5 py-5 md:pt-2",
        !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
      )}
    >
      <div
        className={cn(
          "flex grow flex-col items-center justify-center rounded-xl",
          md ? "bg-interfaceBgLightPaper" : "",
        )}
      >
        <div className="w-full px-5 md:max-w-[360px]">
          <ResetPassword
            errorMessage={errorMessage}
            isLoading={isLoading}
            onSubmit={handleClickSubmit}
          />
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
